import { Trans, withI18n } from '@lingui/react'
import classNames from 'classnames'
import { arrayOf, bool, number, oneOf, shape, string } from 'prop-types'

import { OFFER_TYPE_SAE } from 'shared/consts/marketplace'
import {
  AVAILABLE_PRESCRIPTION_TYPES,
  AVAILABLE_PRODUCT_TEMPLATES,
  PRESCRIPTION_TYPE_NO_PRESCRIPTION,
} from 'shared/consts/productTemplates'
import { TENANT_COM } from 'shared/consts/tenants'
import {
  RELEASE_TOGGLE_VARIANT_ATTTRIBUTES_ENABLED,
  VARIANT_ATTRIBUTES,
} from 'shared/experiments/consts'
import {
  isExperimentVariableTrue,
  isMarketplace,
} from 'shared/experiments/utils/featureFlags'
import { isRebrandingActive } from 'shared/experiments/utils/rebranding'
import { isMobile } from 'shared/utils/deviceClassUtils'
import { isEmpty } from 'shared/utils/objectUtils'
import Button from 'views/components/atoms/Button'
import IconCountdown from 'views/components/atoms/Icon/IconCountdown'
import Modal from 'views/components/molecules/Modal'
import OfferBox from 'views/components/molecules/OfferBox'
import DeliveryCosts from 'views/components/organisms/DeliveryCosts'
import NowCountDown from 'views/components/organisms/NowCountdown'
import { shouldRenderNowCountdown } from 'views/components/organisms/NowCountdown/helpers'
import ProductConsultation from 'views/components/organisms/ProductConsultation'
import ProductStatus from 'views/components/organisms/ProductStatus'
import { hasSaeVariant } from 'views/components/organisms/ProductTemplate/helpers'
import ProductVariantsBody from 'views/components/organisms/ProductVariants/ProductVariantsBody'
import ProductVariantsHeader from 'views/components/organisms/ProductVariants/ProductVariantsHeader'
import ProductVariantsOutOfTradeMessage from 'views/components/organisms/ProductVariants/ProductVariantsOutOfTradeMessage'
import SellerBox from 'views/components/organisms/SellerBox'
import SocialPopup from 'views/components/organisms/SocialPopup'
import Tenant from 'views/components/utils/Tenant'
import {
  ADVICE_MODAL_ID,
  DELIVERY_INFO_MODAL_ID,
  SOCIAL_MODAL_ID,
} from 'views/consts'
import ProductVariantAttributes from 'views/containers/browser/ProductVariantsAttributesContainer/index'
import useMountPoints from 'views/enhancers/useMountPoints'
import useShopAndRuntimeConfig from 'views/enhancers/useShopAndRuntimeConfig'
import withShopAndRuntimeConfig from 'views/enhancers/withShopAndRuntimeConfig'
import {
  isProductAvailable,
  isProductOutOfTrade,
  isVariantVisible,
} from 'views/utils/productUtils'
import {
  i18nType,
  ProductType,
  ProductVariantType,
} from 'views/utils/propTypes'

import AddToCart from './AddToCart'
import BuyBoxIconList from './BuyBoxIconList'

import './BuyBoxWithVariants.scss'

const BuyBoxWithVariants = ({
  currentVariantId,
  disableAddToCartButton,
  displayNowWidget,
  groupName,
  i18n,
  isProductVariantOutOfTrade,
  maxQuantity,
  offerId,
  prescriptionType,
  product,
  indicationUrl,
  productTemplate,
  radioButton,
  renderCrossSellInsteadOfBuyBox,
  shippingData,
  shouldRenderERxSelectModal,
  showAddWishList,
  showCountDownPlaceholder,
  showOdsWidgets,
  showSocials,
  variants,
  isMasterPage,
}) => {
  const {
    id,
    upid,
    stockStatus,
    pharmaObl,
    prices,
    name,
    canonicalURL,
    media,
    odsPartners,
    ICD10,
  } = product
  const {
    publicRuntimeConfig: {
      pageProperties: { experiments = [], tenant },
      publicConfig: { deviceClass },
    },
  } = useShopAndRuntimeConfig()
  const mountPoints = useMountPoints()

  const isVariantAttributeFlag = isExperimentVariableTrue(
    VARIANT_ATTRIBUTES,
    RELEASE_TOGGLE_VARIANT_ATTTRIBUTES_ENABLED,
    experiments
  )

  const visibleVariants = variants?.filter(variant =>
    isVariantVisible(currentVariantId, variant)
  )
  const currentVariant = visibleVariants.find(
    variant => variant.id === currentVariantId
  )

  const hasVariants = visibleVariants?.length > 0
  const hasOffers = currentVariant?.offers?.length
  const bestOffer = hasOffers
    ? currentVariant.offers.find(offer => offer.offerId === offerId)
    : null
  const hasMultiOffers = hasOffers > 1
  const shouldRenderSellerBox =
    isMarketplace(experiments, tenant) &&
    !!hasOffers &&
    !isEmpty(bestOffer?.seller || {})
  const shouldRenderCountdown =
    displayNowWidget &&
    isProductAvailable(stockStatus) &&
    prescriptionType === PRESCRIPTION_TYPE_NO_PRESCRIPTION &&
    (shouldRenderNowCountdown() || showCountDownPlaceholder)
  const hasVariantsBelowBox = visibleVariants?.length > 1
  const isOfferTypeSae =
    hasMultiOffers &&
    currentVariant.offers.some(({ offerType }) => offerType === OFFER_TYPE_SAE)
  const isRebrandingEnabled = isRebrandingActive(experiments)
  const isOutOfTrade = isProductOutOfTrade(stockStatus)
  const isRebrandingAndOutOfTrade = isRebrandingEnabled && isOutOfTrade

  const uniqueSellers = hasMultiOffers
    ? [...new Set(currentVariant.offers.map(offer => offer.seller.displayName))]
    : null

  let sellersCount = uniqueSellers ? uniqueSellers.length : 0
  sellersCount = Math.max(isOfferTypeSae ? sellersCount - 1 : sellersCount, 0)

  const bestOfferPrice = hasMultiOffers
    ? currentVariant.offers.reduce((a, b) => {
        return a.offerPrice.price < b.offerPrice.price ? a : b
      })
    : null

  let lowestPrice = 0.0

  variants?.forEach(variant => {
    variant.offers?.forEach(offer => {
      const price = offer.offerPrice.price || 0.0
      if (lowestPrice === 0.0 || lowestPrice > price) {
        lowestPrice = price
      }
    })
  })
  const shouldRenderBuyBoxIconList =
    (isMobile(deviceClass) && isVariantAttributeFlag) || !isVariantAttributeFlag

  const shouldRenderOfferPrice =
    isMarketplace(experiments, tenant) && bestOfferPrice
  const shouldDisplayOutOfTradeMessage =
    isProductVariantOutOfTrade && !hasVariants

  const variantOffer = currentVariant?.offers?.find(
    offer => offer.offerId === currentVariant?.offerId
  )

  const buyBoxClassName = classNames(
    'u-margin-large--left u-margin-large--right u-margin--left@small-tiny u-margin--right@small-tiny o-BuyBoxWithVariants__buy-box',
    {
      'u-border--top u-padding--top': !shouldRenderCountdown,
      'u-padding--bottom u-padding-large--bottom@medium': !hasVariantsBelowBox,
      'u-padding-large--bottom': isVariantAttributeFlag,
    }
  )

  return (
    <>
      {shouldDisplayOutOfTradeMessage && <ProductVariantsOutOfTradeMessage />}

      {hasVariants && !isVariantAttributeFlag && !isRebrandingAndOutOfTrade && (
        <ProductVariantsHeader
          productTemplate={productTemplate}
          prescriptionType={prescriptionType}
          pharmaObl={pharmaObl}
          offerType={variantOffer?.offerType}
        />
      )}
      <div className="u-margin-large--bottom@medium u-margin--bottom u-background-color--greySky u-border-radius o-BuyBoxWithVariants__wrapper">
        {hasVariants && (
          <div className="u-padding-x-small--sides u-padding-x-small--top">
            <ProductVariantsBody
              variants={[currentVariant]}
              productTemplate={productTemplate}
              prescriptionType={prescriptionType}
              currentVariantId={currentVariantId}
              groupName={groupName}
              radioButton={radioButton}
              lowestPrice={lowestPrice}
              isMasterPage={isMasterPage}
            />
            {isVariantAttributeFlag && (
              <div
                id="h_ProductVariantAttributes"
                data-clientside-hook="variantAttributes-container"
              >
                <ProductVariantAttributes
                  product={product}
                  isMasterPage={isMasterPage}
                  variants={variants}
                  prescriptionType={prescriptionType}
                />
              </div>
            )}
            <Tenant tenant={TENANT_COM}>
              {() => (
                <div
                  className="o-BuyBoxWithVariants__nowCountdown u-padding-x-small--sides u-padding--sides@medium"
                  aria-hidden={shouldRenderCountdown ? null : 'true'}
                >
                  <div className="u-border--top o-BuyBoxWithVariants__nowCountdown__container">
                    {isRebrandingEnabled && <IconCountdown />}
                    <NowCountDown
                      showCountDownPlaceholder={showCountDownPlaceholder}
                    />
                  </div>
                </div>
              )}
            </Tenant>
          </div>
        )}

        {!renderCrossSellInsteadOfBuyBox && (
          <div className={buyBoxClassName}>
            <div id="addToCardButton" hidden={isMasterPage}>
              <AddToCart
                id={id}
                upid={upid}
                prices={prices}
                stockStatus={stockStatus}
                odsPartners={odsPartners}
                indicationUrl={indicationUrl}
                productTemplate={productTemplate}
                prescriptionType={prescriptionType}
                maxQuantity={maxQuantity}
                showOdsWidgets={showOdsWidgets}
                ICD10={ICD10}
                offerId={product.offerId}
                offerType={bestOffer?.offerType}
                shouldRenderERxSelectModal={shouldRenderERxSelectModal}
                isMarketplaceProduct={Boolean(
                  currentVariant?.isMarketplaceProduct
                )}
                disableAddToCartButton={disableAddToCartButton}
              />
            </div>
            {isVariantAttributeFlag && (
              <div id="applyButtonWrapper" hidden={!isMasterPage}>
                <div className="l-flex u-margin--bottom@medium u-margin-large--bottom@small-tiny o-AddToCart--status">
                  <div
                    className="l-flex__primary"
                    id="stockStatus"
                    hidden={isMasterPage}
                  >
                    <ProductStatus stockStatus={stockStatus} />
                  </div>
                  <div
                    id="variantAttributesMessagesContainer"
                    style={{ visibility: isMasterPage ? 'visible' : 'hidden' }}
                  >
                    <p
                      className="u-font-weight--bold u-no-margin u-color--info"
                      data-qa-id="product-status-qa-id"
                      id="noCombinationAvailableOptionMessage"
                      style={{ display: 'none' }}
                    >
                      <Trans id="variantAttributes.message.notPossible" />
                    </p>
                    <p
                      className="u-font-weight--bold u-no-margin"
                      data-qa-id="product-status-qa-id"
                      id="configureProductMessage"
                      style={{ display: isMasterPage ? 'flex' : 'none' }}
                    >
                      <Trans id="variantAttributes.message.configureProduct" />
                    </p>

                    <p
                      className="u-font-weight--bold u-no-margin u-color--red"
                      data-qa-id="product-status-qa-id"
                      id="selectAllVariantAttributesMessage"
                      style={{ display: 'none' }}
                    >
                      <Trans id="variantAttributes.message.configureProduct" />
                    </p>
                  </div>
                </div>
                <Button
                  style="buttonPrimary"
                  id="applyButtonOnPDP"
                  className="a-h3 u-font-weight--normal u-no-margin--bottom"
                >
                  <Trans id="general.label.link.apply" />
                </Button>
              </div>
            )}
            {shouldRenderSellerBox && (
              <SellerBox
                sellerId={bestOffer.seller.sellerId}
                sellerName={bestOffer.seller.displayName}
                offerType={bestOffer.offerType}
                embedSellerModal
              />
            )}
          </div>
        )}

        {hasVariantsBelowBox && !isVariantAttributeFlag && (
          <>
            <div className="u-padding-large--sides o-BuyBoxWithVariants__package-size__title">
              <p className="u-no-margin u-color--grey u-font-weight--bold u-padding--ends o-BuyBoxWithVariants__package-size__title-text">
                <Trans id="buybox.headLine.packageSize" />
              </p>
            </div>
            <div className="u-padding-x-small--sides o-BuyBoxWithVariants__variants-list">
              <ProductVariantsBody
                variants={visibleVariants}
                productTemplate={productTemplate}
                prescriptionType={prescriptionType}
                currentVariantId={currentVariantId}
                groupName={groupName}
                radioButton={radioButton}
              />
            </div>
          </>
        )}
      </div>
      {!shouldDisplayOutOfTradeMessage &&
        !isMasterPage &&
        shouldRenderBuyBoxIconList && (
          <BuyBoxIconList
            showAddWishList={showAddWishList}
            showSocials={showSocials}
            showPersonalAdvice={hasSaeVariant(experiments, tenant, variants)}
            isMasterPage={isMasterPage}
          />
        )}
      {shouldRenderOfferPrice && !isMasterPage && (
        <div className="u-margin-large--top u-margin-large--bottom">
          <OfferBox
            offerTypeSae={isOfferTypeSae}
            sellersCount={sellersCount}
            bestOfferPrice={bestOfferPrice.offerPrice}
            url={mountPoints.getOffersUrl(upid)}
          />
        </div>
      )}
      <Modal
        id={ADVICE_MODAL_ID}
        title={i18n._('buyBox.modal.advice.headline')}
        size="medium"
      >
        <ProductConsultation name={name} id={id} />
      </Modal>
      <Modal
        id={DELIVERY_INFO_MODAL_ID}
        title={i18n._('buyBox.modal.delivery.headline')}
        size="medium"
      >
        <DeliveryCosts shippingData={shippingData} />
      </Modal>
      {showSocials && (
        <Modal
          id={SOCIAL_MODAL_ID}
          title={i18n._('socialModal.label.title')}
          size="medium"
        >
          <SocialPopup
            canonicalURL={canonicalURL}
            productName={name}
            productId={id}
            productMedia={media}
          />
        </Modal>
      )}
    </>
  )
}

BuyBoxWithVariants.propTypes = {
  product: shape(ProductType).isRequired,
  variants: arrayOf(shape(ProductVariantType)),
  currentVariantId: string,
  disableAddToCartButton: bool,
  displayNowWidget: bool.isRequired,
  showCountDownPlaceholder: bool.isRequired,
  groupName: string,
  indicationUrl: string,
  radioButton: bool,
  superScripts: shape({}),
  productTemplate: oneOf(AVAILABLE_PRODUCT_TEMPLATES).isRequired,
  prescriptionType: oneOf(AVAILABLE_PRESCRIPTION_TYPES.ALL),
  isProductVariantOutOfTrade: bool,
  maxQuantity: number.isRequired,
  offerId: string,
  renderCrossSellInsteadOfBuyBox: bool,
  showOdsWidgets: bool,
  i18n: i18nType.isRequired,
  shippingData: shape({ carriers: arrayOf(shape({})) }),
  shouldRenderERxSelectModal: bool,
  showAddWishList: bool,
  showSocials: bool,
  isMasterPage: bool,
}

BuyBoxWithVariants.defaultProps = {
  disableAddToCartButton: false,
  variants: [],
  currentVariantId: null,
  groupName: 'packageVariants',
  radioButton: false,
  superScripts: {},
  prescriptionType: PRESCRIPTION_TYPE_NO_PRESCRIPTION,
  isProductVariantOutOfTrade: false,
  renderCrossSellInsteadOfBuyBox: false,
  showOdsWidgets: false,
  shouldRenderERxSelectModal: false,
  showAddWishList: false,
  showSocials: false,
  shippingData: { carriers: [] },
  isMasterPage: false,
}

export default withI18n()(BuyBoxWithVariants, withShopAndRuntimeConfig)
